'use client';

import { Box, Container, List, ListItem, Typography, useTheme } from '@mui/material';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import {
  ComponentBlocksProsAndConsTable,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types';

import { StrapiImage } from '../../Utility/StrapiImage';

const ColTitle = ({ title }: { title?: string }) => (
  <Typography
    variant="h4"
    sx={{ mb: 2, textAlign: 'center', fontWeight: 700, whiteSpace: 'nowrap' }}
  >
    {title}
  </Typography>
);

export function ProsAndConsTable({ block }: { block: ComponentBlocksProsAndConsTable }) {
  const theme = useTheme();
  const { Title, ProList, ConList, LeftColText = 'Pros', RightColText = 'Cons' } = block;

  return (
    <CustomBackground
      backgroundOptions={(block?.ProsAndConsBackground ?? undefined) as StrapiBackgroundOptions}
      displayOptions={
        {
          ...block?.ProsAndConsDisplayOptions,
          ContentsWidth:
            block?.ProsAndConsDisplayOptions?.ContentsWidth ??
            Enum_Componentshareddisplayoptions_Contentswidth.Wide,
          PaddingTop: 'md',
          PaddingBottom: 'md',
          PaddingInline: 'sm',
        } as StrapiDisplayOptions
      }
      boxProps={{
        // @ts-expect-error - this does work, but types are not correct
        'data-component': 'ProsAndConsTable',
      }}
    >
      <Box sx={{ mx: 'auto' }}>
        {Title && (
          <Container maxWidth="md" sx={{ textAlign: 'center', px: 2 }}>
            <Typography
              variant="h2"
              fontWeight="bold"
              sx={{ mt: 6, mb: 8, textAlign: 'center', lineHeight: 1.2 }}
            >
              {Title}
            </Typography>
          </Container>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
            },
            gap: 4,

            '& > * ': {
              backgroundColor: theme.palette.common.white,
              borderRadius: 8,
              p: 3,
            },
          }}
        >
          <Box>
            <ColTitle title={LeftColText} />
            <List>
              {ProList?.filter((item) => item?.ProItemDescription)?.map((item, i) => (
                <ListItem key={i} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  {item?.ProItemLogo && (
                    <Box sx={{ mr: 2, mt: '0.25rem' }}>
                      <StrapiImage image={item.ProItemLogo} size="small" width={24} height={24} />
                    </Box>
                  )}
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
                      {!item?.ProItemLogo && <>• &nbsp; &nbsp;</>}
                      {item?.ProItemTitle}
                    </Typography>
                    {item?.ProItemDescription && (
                      <GlobalBlockRenderer content={item.ProItemDescription} />
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>

          <Box>
            <ColTitle title={RightColText} />
            <List>
              {ConList?.filter((item) => item?.ConItemDescription)?.map((item, i) => (
                <ListItem key={i} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  {item?.ConItemLogo && (
                    <Box sx={{ mr: 2, mt: '0.25rem' }}>
                      <StrapiImage image={item.ConItemLogo} size="small" width={24} height={24} />
                    </Box>
                  )}
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
                      {!item?.ConItemLogo && <>• &nbsp; &nbsp;</>}
                      {item?.ConItemTitle}
                    </Typography>
                    {item?.ConItemDescription && (
                      <GlobalBlockRenderer content={item.ConItemDescription} />
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      </Box>
    </CustomBackground>
  );
}
